import React, { useEffect, useState, useCallback } from 'react'
import LoadingOverlay from 'react-loading-overlay'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import axios from 'axios'
import moment from 'moment-timezone'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  makeStyles,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Box,
  Button
} from '@material-ui/core'
import ValidationAndLimitLengthInput from '../atoms/ValidationAndLimitLengthInput'
import CenteredSubmitButton from '../molecules/CenteredSubmitButton'
import DateTimePicker from '../molecules/DateTimePicker'
import { setDialog } from '../../store/actions/dialog'
import { Notification } from '../../types/Notification'

const useStyles = makeStyles(theme => ({
  flexCenter: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  loadingOverlay: {
    position: 'absolute',
    height: '100%',
    width: '100%'
  }
}))

const NotificationPushModal: React.FC<
  {
    notificationId: number
    isOpen: boolean
    closeHandler: () => void
  } & ReturnType<typeof mapDispatchToProps>
> = ({ notificationId, isOpen, closeHandler, setDialog }) => {
  const classes = useStyles()
  const handleClose = useCallback(() => {
    closeHandler()
  }, [closeHandler])
  const [validateState, setValidateState] = useState(false)
  const [notification, setnotification] = useState<{
    id: number
    release_date: Date
    close_date: Date | null
    push_message: string
    push_member_flg: number
    push_reserved_date: Date | null
    push_at: string
    category_member_flg: number
  }>({
    id: null,
    release_date: null,
    close_date: null,
    push_message: '',
    push_member_flg: 1,
    push_reserved_date: null,
    push_at: '',
    category_member_flg: null
  })
  const [push, setPush] = useState<{
    id: number
    push_message: string
    push_member_flg: number
    push_reserved_date: Date | null
  }>({
    id: null,
    push_message: '',
    push_member_flg: 0,
    push_reserved_date: null
  })
  const [reserve, setReserve] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get<{ notification: Notification; member_flg: number }>(
        `/notifications/push-byarticle/${notificationId}`
      )
      let message = ''
      let memberFlg = data.member_flg
      const releaseDate = moment(data.notification.release_date).toDate()
      const _reserveDate = data.notification.push_reserved_date
      let reservedDate: Date = null

      // DBにpush通知の登録値がある場合
      if (data.notification.push_message) {
        message = data.notification.push_message
        memberFlg = data.notification.push_member_flg
      }
      if (_reserveDate) {
        setReserve(true)
        reservedDate = moment(_reserveDate).toDate()
      } else {
        setReserve(false)
        reservedDate = releaseDate
      }

      // 時間が5分単位でなければ分を切り上げる。
      const LastDigitOfMinute = (moment(reservedDate).minutes() / 1) % 10 // 分の下一桁
      if (LastDigitOfMinute !== 0 && LastDigitOfMinute !== 5) {
        let diff = 0
        if (LastDigitOfMinute < 5) {
          diff = 5 - LastDigitOfMinute
        }
        if (LastDigitOfMinute > 5) {
          diff = 10 - LastDigitOfMinute
        }
        reservedDate = moment(reservedDate).add(diff, 'minutes').toDate()
      }

      setnotification({
        id: data.notification.id,
        release_date: releaseDate,
        close_date: data.notification.close_date
          ? moment(data.notification.close_date).toDate()
          : null,
        push_message: data.notification.push_message,
        push_member_flg: data.notification.push_member_flg,
        push_reserved_date: _reserveDate ? moment(_reserveDate).toDate() : null,
        push_at: data.notification.push_at,
        category_member_flg: data.member_flg
      })

      setPush({
        id: data.notification.id,
        push_message: message,
        push_member_flg: memberFlg,
        push_reserved_date: reservedDate
      })
    } catch (error) {
      alert(error.message)
    }
  }, [notificationId])

  useEffect(() => {
    if (isOpen) fetch()
  }, [isOpen])

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      try {
        event.preventDefault()
        const pushReservedDate = push.push_reserved_date
        const pushMemberFlg = push.push_member_flg

        // お知らせ取得
        const { data } = await axios.get<{ notification: Notification; member_flg: number }>(
          `/notifications/push-byarticle/${notificationId}`
        )
        const releaseDate = moment(data.notification.release_date).toDate()
        const closeDate = data.notification.close_date
          ? moment(data.notification.close_date).toDate()
          : null
        const categoryMemberFlg = data.member_flg

        // 送信対象チェック
        if (categoryMemberFlg === 1 && pushMemberFlg === 0) {
          alert(
            '送信対象が不正です。会員限定のお知らせを全体（会員＋ユーザー）に通知することはできません。'
          )
          return
        }

        // * * * * * * *
        // 送信予約
        // * * * * * * *
        if (reserve) {
          // 予約日時チェック
          const LastDigitOfMinute = (moment(pushReservedDate).minutes() / 1) % 10 // 分の下一桁
          if (LastDigitOfMinute !== 0 && LastDigitOfMinute !== 5) {
            alert('送信予約日時は5分単位に設定してください。')
            return
          }
          // 予約日時が掲載期間内かチェック
          if (
            pushReservedDate < releaseDate ||
            (closeDate && closeDate < pushReservedDate) ||
            pushReservedDate < moment().toDate()
          ) {
            alert('送信予約日時に、掲載期間外の日時および現在より過去の日時は設定できません。')
            return
          }
          setDialog(
            '確認',
            `【予約配信】プッシュ通知は${
              pushMemberFlg ? '会員のみ' : '全体（会員＋ユーザー）'
            }に一斉送信されます。よろしいですか？`,
            async () => {
              setIsLoading(true)
              await axios.put<Notification>(`/notifications/${notificationId}`, {
                push_message: push.push_message,
                push_member_flg: pushMemberFlg,
                push_reserved_date: moment(pushReservedDate).format('YYYY-MM-DD HH:mm'),
                push_request: null,
                push_result: null,
                push_at: null
              })
              setIsLoading(false)
              closeHandler()
            },
            () => {}
          )

          // * * * * * * *
          // 即時配信
          // * * * * * * *
        } else {
          // 現在日時が掲載期間内かチェック
          if (moment().toDate() < releaseDate || (closeDate && closeDate < moment().toDate())) {
            alert('掲載期間外の通知送信はできません。')
            return
          }
          setDialog(
            '確認',
            `【即時配信】プッシュ通知は${
              pushMemberFlg ? '会員のみ' : '全体（会員＋ユーザー）'
            }に一斉送信されます。よろしいですか？`,
            async () => {
              setIsLoading(true)
              await axios.post(`/notifications/push-byarticle/${notificationId}`, {
                push_message: push.push_message,
                push_member_flg: pushMemberFlg
              })
              setIsLoading(false)
              closeHandler()
            },
            () => {}
          )
        }
      } catch (err) {}
    },
    [setDialog, setIsLoading, closeHandler, reserve, push, notificationId]
  )

  const reservedCancelHandler = useCallback(async () => {
    setDialog(
      '確認',
      `予約を取り消してもよろしいですか？`,
      async () => {
        setIsLoading(true)
        await axios.put<Notification>(`/notifications/${notificationId}`, {
          push_message: '',
          push_member_flg: 0,
          push_reserved_date: null,
          push_request: null,
          push_result: null,
          push_at: null
        })
        setIsLoading(false)
        closeHandler()
      },
      () => {}
    )
  }, [setDialog, setIsLoading, closeHandler, reserve, push])

  return (
    <React.Fragment>
      <Dialog maxWidth="lg" open={isOpen} onClose={handleClose}>
        {isLoading && (
          <LoadingOverlay
            active={isLoading}
            spinner
            className={classes.loadingOverlay}
            styles={{
              overlay: base => ({
                ...base,
                background: 'rgba(255, 255, 255)',
                opacity: 0.4
              }),
              spinner: base => ({
                ...base,
                width: '100px',
                '& svg circle': {
                  stroke: '#3f51b5'
                }
              })
            }}
          />
        )}

        <DialogTitle>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="h5" color="primary">
              プッシュ通知送信
            </Typography>
            {notification.push_at && (
              <Box textAlign="right">
                <Typography variant="caption">
                  {`（最終）送信日時 : ${moment(notification.push_at).format('YYYY/MM/DD HH:mm')}`}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogTitle>
        <DialogContent style={{ width: 600 }}>
          <Typography variant="body1" paragraph>
            お知らせに関したプッシュ通知を配信することができます。
          </Typography>
          <Box mb={2}>
            <FormControl component="fieldset" style={{ marginLeft: '1rem' }}>
              <RadioGroup
                aria-label="reserve"
                name="reserve"
                value={reserve}
                onChange={() => {}}
                row
              >
                <FormControlLabel
                  value={false}
                  control={<Radio onChange={() => setReserve(false)} required />}
                  label="即時配信"
                />
                <FormControlLabel
                  value={true}
                  control={<Radio onChange={() => setReserve(true)} required />}
                  label="予約配信"
                />
              </RadioGroup>
            </FormControl>
          </Box>
          <Typography variant="subtitle2">タイトル</Typography>
          <Box mb={2}>
            <ValidationAndLimitLengthInput
              value={push.push_message}
              label=""
              required={false}
              variant="outlined"
              fullWidth
              inputHandler={input => setPush({ ...push, push_message: input })}
              invalidStr={/["'<>&]/}
              strLength={100}
              validationErrorMessage="使用できない記号が含まれています。"
              strLengthErrorMessage="文字数が100文字を超えています。文字数を調節してください。"
              validationStateChange={isFullFilled => {
                setValidateState(isFullFilled)
              }}
            />
            <Box>
              <Typography variant="caption" color="error" style={{ marginLeft: '12px' }}>
                ※端末により40Byte以降の文字が表示されない場合もございます。
              </Typography>
              <br />
              <Typography variant="caption" color="error" style={{ marginLeft: '24px' }}>
                通知文字数が長くならないようご注意ください。
              </Typography>
              <br />
              <Typography variant="caption" color="error" style={{ marginLeft: '12px' }}>
                ※以下の記号類は、端末側で文字化けする可能性がありますので、使用しないでください。
              </Typography>
              <br />
              <Typography variant="caption" color="error" style={{ marginLeft: '24px' }}>
                「"」「&#39;」「&lt;」「&gt;」「&amp;」
              </Typography>
            </Box>
          </Box>
          <Typography variant="subtitle2">送信対象</Typography>
          <Box mb={2}>
            <FormControl component="fieldset" style={{ marginLeft: '1rem' }}>
              <RadioGroup
                aria-label="memberFlg"
                name="memberFlg"
                value={push.push_member_flg}
                onChange={() => {}}
                row
              >
                <FormControlLabel
                  value={1}
                  control={
                    <Radio onChange={() => setPush({ ...push, push_member_flg: 1 })} required />
                  }
                  label="会員のみ"
                />
                <FormControlLabel
                  value={0}
                  control={
                    <Radio onChange={() => setPush({ ...push, push_member_flg: 0 })} required />
                  }
                  disabled={notification.category_member_flg === 1 && true}
                  label="全体（会員＋ユーザー）"
                />
              </RadioGroup>
            </FormControl>
          </Box>
          {reserve && (
            <Box>
              <Typography variant="subtitle2">
                予約配信日時
                <Typography variant="caption" style={{ marginLeft: '12px' }}>
                  ※5分単位で指定してください。
                </Typography>
              </Typography>

              <Box mb={2} width={400}>
                <DateTimePicker
                  value={push.push_reserved_date}
                  pickerHandler={(date: Date | null) => {
                    setPush({
                      ...push,
                      push_reserved_date: date
                    })
                  }}
                  required={reserve}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions className={classes.flexCenter}>
          <form onSubmit={event => handleSubmit(event)}>
            <Box display="flex">
              <CenteredSubmitButton
                submittable={
                  reserve
                    ? validateState && push.push_message && push.push_reserved_date !== null
                      ? true
                      : false
                    : validateState && push.push_message
                    ? true
                    : false
                }
                resettable
                resetText="キャンセル"
                submitText={reserve ? '登録' : '送信'}
                submitHandler={() => {}}
                resetHandler={handleClose}
                size="large"
              />
              {reserve && (
                <Button
                  variant="contained"
                  disabled={notification.push_reserved_date && !notification.push_at ? false : true}
                  color="primary"
                  size="medium"
                  style={{ height: 42, marginTop: 16 }}
                  onClick={reservedCancelHandler}
                >
                  予約を取り消す
                </Button>
              )}
            </Box>
          </form>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  )
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    setDialog: (
      title: string,
      content: string,
      okHandler: () => void,
      ngHandler: () => void,
      noCancel?: boolean
    ) => dispatch(setDialog(title, content, okHandler, ngHandler, noCancel || false))
  }
}

export default connect(null, mapDispatchToProps)(NotificationPushModal)
